import { useTranslation } from "react-i18next";
import { Users } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Popover, PopoverTrigger } from "@/components/ui/popover";
import { shortAge, type PresentUser } from "@/realtime/usePresence.js";

/**
 * "Who's online" in the header (ADR-026 Phase 5).
 *
 * A count that opens the roster, rather than a row of avatars: the realm can have dozens of people connected, and a
 * strip of faces would either overflow the header or silently truncate the very information it exists to show.
 *
 * **A Popover, not a `role="menu"`.** The roster is content, not a list of commands — and static text inside a menu is
 * skipped entirely by screen readers in application mode (the repo's own convention).
 */
export function OnlineUsers({ users, selfId }: { users: PresentUser[]; selfId: string | null }) {
  const { t } = useTranslation();
  // **You are not "online" to yourself.** The roster answers "who ELSE is here" — counting yourself makes the same
  // number mean something different to every viewer (you'd read 2 while a colleague alone with you reads 2 as well,
  // but a lone user would read 1 and think someone was around). The SERVER still broadcasts the full roster, because
  // that is the shared truth other clients need; only this view drops the viewer.
  const others = selfId ? users.filter((u) => u.userId !== selfId) : users;
  // Nothing to show when you are alone, or before presence arrives — an empty "0 online" would read as a statement of
  // fact rather than "not loaded yet".
  if (others.length === 0) return null;

  return (
    <PopoverTrigger>
      <Button
        variant="ghost"
        size="sm"
        className="gap-1.5"
        aria-label={t("shell.othersOnline", { count: others.length })}
      >
        <Users aria-hidden="true" className="size-4" />
        <span className="text-sm tabular-nums">{others.length}</span>
      </Button>
      <Popover className="w-64 p-1">
        <p className="text-muted-foreground px-2 py-1.5 text-xs font-medium">{t("shell.alsoOnline")}</p>
        <ul className="max-h-72 list-none overflow-auto p-0">
          {others.map((u) => (
            <li key={u.userId} className="flex items-center justify-between gap-2 rounded-sm px-2 py-1.5 text-sm">
              <span className="min-w-0 truncate">
                {u.name}
                {/* More than one tab open is worth showing: it explains why someone appears active in two places. */}
                {u.connections > 1 ? <span className="text-muted-foreground"> ×{u.connections}</span> : null}
              </span>
              {/* A relative age, not a timestamp — "since 14:02" makes the reader do arithmetic. */}
              <span className="text-muted-foreground shrink-0 text-xs tabular-nums">{shortAge(u.since)}</span>
            </li>
          ))}
        </ul>
      </Popover>
    </PopoverTrigger>
  );
}
